import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import RecordButton from '../components/RecordButton';
import Logo from '../components/Logo';
import useAudioRecorder from '../hooks/useAudioRecorder';
import useSounds from '../hooks/useSounds';
import { transcribeAudio, evaluateIELTSSpeaking } from '../services/api';
import {
  IELTSExamDuration,
  IELTSSpeakingPart,
  IELTSSpeakingEvaluation,
  SPEAKING_TOPICS_PART1,
  SPEAKING_TOPICS_PART2,
  SPEAKING_TOPICS_PART3,
} from '../types/ielts';

interface LocationState {
  duration: IELTSExamDuration;
}

// IELTS band score rounding: .25 and .75 round DOWN
// e.g., 6.25 -> 6.0, 6.5 -> 6.5, 6.75 -> 6.5, 7.0 -> 7.0
// With 4 integer component scores, possible decimals are only: .00, .25, .50, .75
const roundIELTSBand = (num: number): number => {
  const decimal = num - Math.floor(num);
  // .00 and .25 round to .0; .50 and .75 round to .5
  if (decimal < 0.5) {
    return Math.floor(num);
  } else {
    return Math.floor(num) + 0.5;
  }
};

const IELTSSpeakingPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { playSound } = useSounds();

  const state = location.state as LocationState;
  const duration = state?.duration || 'short';

  // Determine which parts to include based on duration
  const getPartsCount = () => {
    switch (duration) {
      case 'short': return 1;
      case 'medium': return 2;
      case 'full': return 3;
      default: return 1;
    }
  };

  const partsCount = getPartsCount();

  // Generate speaking parts with questions - topic-based structure
  const generateParts = (): IELTSSpeakingPart[] => {
    const parts: IELTSSpeakingPart[] = [];

    if (partsCount >= 1) {
      // Pick a random topic for Part 1
      const randomTopic = SPEAKING_TOPICS_PART1[Math.floor(Math.random() * SPEAKING_TOPICS_PART1.length)];
      parts.push({
        part: 1,
        title: `Introduction & Interview: ${randomTopic.topic}`,
        description: randomTopic.introduction,
        timeMinutes: 5,
        questions: randomTopic.questions,
      });
    }

    if (partsCount >= 2) {
      // Pick a random topic for Part 2
      const randomTopic = SPEAKING_TOPICS_PART2[Math.floor(Math.random() * SPEAKING_TOPICS_PART2.length)];
      parts.push({
        part: 2,
        title: `Individual Long Turn: ${randomTopic.topic}`,
        description: randomTopic.introduction,
        timeMinutes: 4,
        questions: randomTopic.questions,
        preparationTime: 60,
      });
    }

    if (partsCount >= 3) {
      // Pick a random topic for Part 3
      const randomTopic = SPEAKING_TOPICS_PART3[Math.floor(Math.random() * SPEAKING_TOPICS_PART3.length)];
      parts.push({
        part: 3,
        title: `Two-way Discussion: ${randomTopic.topic}`,
        description: randomTopic.introduction,
        timeMinutes: 5,
        questions: randomTopic.questions,
      });
    }

    return parts;
  };

  const [parts] = useState<IELTSSpeakingPart[]>(generateParts);
  const [currentPartIndex, setCurrentPartIndex] = useState(0);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [responses, setResponses] = useState<{ part: number; questionIndex: number; transcription: string }[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [preparationTimer, setPreparationTimer] = useState<number | null>(null);
  const [isPreparationTime, setIsPreparationTime] = useState(false);
  const [evaluation, setEvaluation] = useState<IELTSSpeakingEvaluation | null>(null);
  const [status, setStatus] = useState<'intro' | 'in-progress' | 'completed'>('intro');
  const [questionTimer, setQuestionTimer] = useState<number | null>(null);
  const processedBlobRef = useRef<Blob | null>(null);
  const autoStopTriggeredRef = useRef(false);

  // Part 1 question time limit (45 seconds)
  const PART1_TIME_LIMIT = 45;
  // Part 2 speaking time limit (2 minutes)
  const PART2_TIME_LIMIT = 120;
  // Part 3 question time limit (75 seconds)
  const PART3_TIME_LIMIT = 75;

  const {
    isRecording,
    audioBlob,
    error: recordingError,
    recordingTime,
    startRecording,
    stopRecording,
    resetRecording,
  } = useAudioRecorder();

  const currentPart = parts[currentPartIndex];
  const currentQuestion = currentPart?.questions[currentQuestionIndex];
  // Part 2 counts as 1 question (single long turn), not 5
  const totalQuestions = parts.reduce((sum, part) => sum + (part.part === 2 ? 1 : part.questions.length), 0);
  const answeredQuestions = responses.length;

  // Block copy/paste/cut during the test
  useEffect(() => {
    const preventCopyPaste = (e: ClipboardEvent) => {
      if (status === 'in-progress') {
        e.preventDefault();
        return false;
      }
    };

    const preventContextMenu = (e: MouseEvent) => {
      if (status === 'in-progress') {
        e.preventDefault();
        return false;
      }
    };

    document.addEventListener('copy', preventCopyPaste);
    document.addEventListener('paste', preventCopyPaste);
    document.addEventListener('cut', preventCopyPaste);
    document.addEventListener('contextmenu', preventContextMenu);

    return () => {
      document.removeEventListener('copy', preventCopyPaste);
      document.removeEventListener('paste', preventCopyPaste);
      document.removeEventListener('cut', preventCopyPaste);
      document.removeEventListener('contextmenu', preventContextMenu);
    };
  }, [status]);

  // Handle Part 2 preparation time - auto-start recording when prep time ends
  useEffect(() => {
    if (isPreparationTime && preparationTimer !== null && preparationTimer > 0) {
      const timer = setTimeout(() => {
        setPreparationTimer(preparationTimer - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (isPreparationTime && preparationTimer === 0) {
      setIsPreparationTime(false);
      playSound('startRecording');
      // Auto-start recording for Part 2
      setQuestionTimer(PART2_TIME_LIMIT);
      autoStopTriggeredRef.current = false;
      startRecording();
    }
  }, [isPreparationTime, preparationTimer, playSound, startRecording]);

  // Handle Part 1, Part 2 & Part 3 question time limits
  useEffect(() => {
    if (isRecording && questionTimer !== null && questionTimer > 0) {
      const timer = setTimeout(() => {
        setQuestionTimer(questionTimer - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (isRecording && questionTimer === 0 && !autoStopTriggeredRef.current) {
      // Time's up - auto-stop recording
      autoStopTriggeredRef.current = true;
      stopRecording();
    }
  }, [isRecording, questionTimer, stopRecording]);

  // Process audio when recording stops
  useEffect(() => {
    const processAudio = async () => {
      if (audioBlob && audioBlob !== processedBlobRef.current && !isProcessing) {
        processedBlobRef.current = audioBlob;
        setIsProcessing(true);

        try {
          const result = await transcribeAudio(audioBlob);

          setResponses(prev => [
            ...prev,
            {
              part: currentPart.part,
              questionIndex: currentQuestionIndex,
              transcription: result.text,
            },
          ]);

          playSound('complete');

          // Move to next question or part
          // Part 2 is a single long turn - skip remaining questions after one recording
          const isPartComplete = currentPart.part === 2 || currentQuestionIndex >= currentPart.questions.length - 1;
          if (!isPartComplete) {
            setCurrentQuestionIndex(prev => prev + 1);
          } else if (currentPartIndex < parts.length - 1) {
            setCurrentPartIndex(prev => prev + 1);
            setCurrentQuestionIndex(0);

            // Check if next part is Part 2 (has preparation time)
            const nextPart = parts[currentPartIndex + 1];
            if (nextPart.preparationTime) {
              setIsPreparationTime(true);
              setPreparationTimer(nextPart.preparationTime);
            }
          } else {
            // All questions answered, generate evaluation
            await generateEvaluation([
              ...responses,
              {
                part: currentPart.part,
                questionIndex: currentQuestionIndex,
                transcription: result.text,
              },
            ]);
            setStatus('completed');
          }

          resetRecording();
          processedBlobRef.current = null;
        } catch (err) {
          console.error('Processing error:', err);
          playSound('error');
        } finally {
          setIsProcessing(false);
        }
      }
    };

    processAudio();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioBlob]);

  const generateEvaluation = async (allResponses: typeof responses) => {
    try {
      // Build responses with question text for the API
      const responsesWithQuestions = allResponses.map(r => {
        const part = parts.find(p => p.part === r.part);
        return {
          part: r.part,
          questionIndex: r.questionIndex,
          question: part?.questions[r.questionIndex] || `Question ${r.questionIndex + 1}`,
          transcription: r.transcription,
        };
      });

      const result = await evaluateIELTSSpeaking({
        responses: responsesWithQuestions,
        duration,
      });

      setEvaluation(result);
      playSound('success');
    } catch (err) {
      console.error('AI evaluation failed, using fallback:', err);
      // Fallback to basic evaluation if API fails
      const avgLength = allResponses.reduce((sum, r) => sum + r.transcription.length, 0) / allResponses.length;
      const baseScore = Math.min(7, Math.max(5, Math.round((avgLength / 60) + 3.5)));
      const overallBand = roundIELTSBand(baseScore);

      setEvaluation({
        overallBand,
        fluencyAndCoherence: baseScore,
        lexicalResource: baseScore,
        grammaticalRange: baseScore,
        pronunciation: baseScore,
        feedback: 'AI evaluation was unavailable. This is an estimated score based on response length. Try again for a detailed assessment.',
        strengths: ['Completed the speaking test', 'Attempted all questions'],
        improvements: ['Try again for detailed AI feedback'],
      });
      playSound('success');
    }
  };

  const handleStart = () => {
    setStatus('in-progress');
    playSound('startRecording');

    // Check if first part has preparation time (Part 2 for medium duration)
    if (currentPart.preparationTime) {
      setIsPreparationTime(true);
      setPreparationTimer(currentPart.preparationTime);
    }
  };

  const handleRecordToggle = async () => {
    if (isRecording) {
      setQuestionTimer(null);
      await stopRecording();
    } else {
      // Start timer for each part
      autoStopTriggeredRef.current = false;
      if (currentPart?.part === 1) {
        setQuestionTimer(PART1_TIME_LIMIT);
      } else if (currentPart?.part === 2) {
        setQuestionTimer(PART2_TIME_LIMIT);
      } else if (currentPart?.part === 3) {
        setQuestionTimer(PART3_TIME_LIMIT);
      }
      await startRecording();
    }
  };

  if (status === 'intro') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white">
        <div className="max-w-3xl mx-auto px-4 py-12">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-800 mb-2">IELTS Speaking Test</h1>
            <p className="text-gray-600">
              {duration === 'short' && 'Quick Practice - Part 1 Only'}
              {duration === 'medium' && 'Standard Practice - Parts 1 & 2'}
              {duration === 'full' && 'Full Exam - All 3 Parts'}
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6 mb-8">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Test Overview</h2>
            <div className="space-y-4">
              {parts.map((part, index) => (
                <div key={part.part} className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
                  <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-emerald-700 font-semibold">{index + 1}</span>
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-800">Part {part.part}: {part.title}</h3>
                    <p className="text-sm text-gray-600">{part.description}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {part.questions.length} question(s) • ~{part.timeMinutes} min
                      {part.preparationTime && ` • ${part.preparationTime}s preparation`}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-emerald-50 rounded-xl p-6 mb-8">
            <h3 className="font-semibold text-emerald-800 mb-2">Tips for Success</h3>
            <ul className="text-sm text-emerald-700 space-y-1">
              <li>- Speak clearly and at a natural pace</li>
              <li>- Develop your answers with examples and details</li>
              <li>- Don't worry about making small mistakes</li>
              <li>- Use a variety of vocabulary and sentence structures</li>
            </ul>
          </div>

          <div className="flex justify-center gap-4">
            <button
              onClick={() => navigate('/app')}
              className="px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors flex items-center gap-2"
              title="Go to Home"
            >
              <Logo size="sm" />
            </button>
            <button
              onClick={() => navigate('/app/ielts')}
              className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors"
            >
              Back to IELTS
            </button>
            <button
              onClick={handleStart}
              className="px-8 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg transition-colors flex items-center gap-2"
            >
              Begin Test
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'completed' && evaluation) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white">
        <div className="max-w-3xl mx-auto px-4 py-12">
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">Test Complete!</h1>
            <p className="text-gray-600">Here's your IELTS Speaking evaluation</p>
          </div>

          {/* Overall Band Score */}
          <div className="bg-white rounded-xl shadow-md p-6 mb-6 text-center">
            <p className="text-gray-600 mb-2">Overall Band Score</p>
            <div className="text-6xl font-bold text-emerald-600 mb-2">{evaluation.overallBand.toFixed(1)}</div>
          </div>

          {/* Detailed Scores */}
          <div className="bg-white rounded-xl shadow-md p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Detailed Scores</h2>
            <div className="grid grid-cols-2 gap-4">
              {[
                { label: 'Fluency & Coherence', score: evaluation.fluencyAndCoherence },
                { label: 'Lexical Resource', score: evaluation.lexicalResource },
                { label: 'Grammatical Range', score: evaluation.grammaticalRange },
                { label: 'Pronunciation', score: evaluation.pronunciation },
              ].map((item) => (
                <div key={item.label} className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600">{item.label}</p>
                  <p className="text-2xl font-bold text-gray-800">{item.score.toFixed(1)}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Feedback */}
          <div className="bg-white rounded-xl shadow-md p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-3">Feedback</h2>
            <p className="text-gray-700 mb-4">{evaluation.feedback}</p>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-green-50 rounded-lg p-4">
                <h3 className="font-medium text-green-800 mb-2">Strengths</h3>
                <ul className="text-sm text-green-700 space-y-1">
                  {evaluation.strengths.map((s, i) => (
                    <li key={i}>+ {s}</li>
                  ))}
                </ul>
              </div>
              <div className="bg-amber-50 rounded-lg p-4">
                <h3 className="font-medium text-amber-800 mb-2">Areas to Improve</h3>
                <ul className="text-sm text-amber-700 space-y-1">
                  {evaluation.improvements.map((s, i) => (
                    <li key={i}>- {s}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-center gap-4">
            <button
              onClick={() => navigate('/app')}
              className="px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors flex items-center gap-2"
              title="Go to Home"
            >
              <Logo size="sm" />
            </button>
            <button
              onClick={() => navigate('/app/ielts')}
              className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors"
            >
              Back to IELTS
            </button>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-lg transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  // In-progress state
  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white">
      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Progress Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">
              Part {currentPart.part} - {currentPart.part === 2 ? 'Topic Card' : `Question ${currentQuestionIndex + 1}/${currentPart.questions.length}`}
            </span>
            <span className="text-sm text-emerald-600 font-medium">
              {answeredQuestions}/{totalQuestions} answered
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-emerald-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(answeredQuestions / totalQuestions) * 100}%` }}
            />
          </div>
        </div>

        {/* Part Header */}
        <div className="bg-emerald-600 text-white rounded-t-xl px-6 py-4">
          <h2 className="text-xl font-semibold">Part {currentPart.part}: {currentPart.title}</h2>
          <p className="text-emerald-100 text-sm">{currentPart.description}</p>
        </div>

        {/* Question Card */}
        <div className="bg-white rounded-b-xl shadow-md p-6 mb-6">
          {isPreparationTime ? (
            <div className="text-center py-8">
              <div className="text-6xl font-bold text-emerald-600 mb-4">{preparationTimer}</div>
              <p className="text-gray-600 mb-4">Preparation Time</p>
              <div className="bg-gray-50 rounded-lg p-4 text-left">
                <p className="text-sm font-semibold text-gray-500 mb-2">Topic Card</p>
                <ul className="list-disc list-inside space-y-2">
                  {currentPart.questions.map((q, i) => (
                    <li key={i} className="text-gray-800">{q}</li>
                  ))}
                </ul>
              </div>
              <p className="text-sm text-gray-500 mt-4">
                Read the topic card and prepare your answer. Speaking will begin automatically.
              </p>
            </div>
          ) : (
            <>
              <div className="mb-6">
                {currentPart.part === 2 ? (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm font-semibold text-gray-500 mb-2">Topic Card</p>
                    <ul className="list-disc list-inside space-y-2">
                      {currentPart.questions.map((q, i) => (
                        <li key={i} className="text-gray-800">{q}</li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <p className="text-lg text-gray-800 whitespace-pre-wrap">{currentQuestion}</p>
                )}
              </div>

              {recordingError && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-700 text-sm">{recordingError}</p>
                </div>
              )}

              {/* Recording Controls */}
              <div className="text-center">
                {isProcessing ? (
                  <div className="py-8">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Processing your response...</p>
                  </div>
                ) : (
                  <>
                    {/* Timer for all parts */}
                    {isRecording && questionTimer !== null && (
                      <div className="mb-4">
                        <div className={`text-3xl font-bold ${questionTimer <= 10 ? 'text-red-500' : 'text-emerald-600'}`}>
                          {currentPart?.part === 2
                            ? `${Math.floor(questionTimer / 60)}:${(questionTimer % 60).toString().padStart(2, '0')}`
                            : `${questionTimer}s`
                          }
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                          <div
                            className={`h-2 rounded-full transition-all duration-1000 ${questionTimer <= 10 ? 'bg-red-500' : 'bg-emerald-600'}`}
                            style={{ width: `${(questionTimer / (currentPart?.part === 1 ? PART1_TIME_LIMIT : currentPart?.part === 2 ? PART2_TIME_LIMIT : PART3_TIME_LIMIT)) * 100}%` }}
                          />
                        </div>
                      </div>
                    )}
                    <RecordButton
                      isRecording={isRecording}
                      isProcessing={isProcessing}
                      recordingTime={recordingTime}
                      onStart={handleRecordToggle}
                      onStop={handleRecordToggle}
                      disabled={false}
                    />
                    <p className="text-gray-600 text-sm mt-4">
                      {isRecording
                        ? 'Recording... Will auto-advance when time is up'
                        : 'Click the microphone to record your answer'}
                    </p>
                  </>
                )}
              </div>
            </>
          )}
        </div>

        {/* Navigation */}
        <div className="flex justify-between">
          <button
            onClick={() => navigate('/app/ielts')}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            Exit Test
          </button>
        </div>
      </div>
    </div>
  );
};

export default IELTSSpeakingPage;
